// src/socket.js
import { io } from 'socket.io-client';
import { getApiBase } from './utils/api';
import { initSocketDebug } from './utils/socketDebug';
import safeStorage from './utils/safeStorage';

const apiBase = getApiBase();

const socket = io(apiBase || undefined, {
    // polling zuerst: Safari/iOS blockiert WebSocket bei initialem Seitenload
    // manchmal durch Content-Blocker oder WKWebView-Restrictions.
    // Socket.IO upgradet nach erfolgreichem Handshake automatisch auf WebSocket.
    transports: ['polling', 'websocket'],
    withCredentials: true,
});

initSocketDebug(socket);

/**
 * Auto-Rejoin bei JEDEM Socket-Connect
 *
 * Wenn eine playerId im localStorage liegt, schicken wir bei jedem 'connect'
 * ein playerRejoin. Der Server-Handler ist idempotent und verarbeitet
 * Duplikate problemlos.
 *
 * Die frühere Variante hat den ersten Connect mit einer Modul-Variable
 * ('isInitialConnect') übersprungen und sich darauf verlassen, dass App.js
 * den initialen Rejoin schickt. Das war eine Race Condition: wenn das Socket
 * 'connect' verpasst wurde (Tab-Suspend, schnelle Reconnects vor App-Mount,
 * Service-Worker etc.), blieb der serverseitige 'socket.data.player'-Cache
 * leer – mit der Folge, dass Handler wie 'selectAnswer' die Fehlermeldung
 * "Du bist keinem Team zugeordnet." lieferten, obwohl der Spieler in der
 * DB einem Team angehört.
 *
 * Konsequenz: Wir rejoinen immer, App.js darf zusätzlich rejoinen (idempotent).
 */
socket.on('connect', () => {
    const playerId = safeStorage.getItem('playerId');
    if (playerId) {
        console.log('[socket] connect – sende playerRejoin für playerId:', playerId);
        socket.emit('playerRejoin', { playerId });
    }
});

/**
 * Eigene Spieler-Löschung abfangen (z.B. durch Inaktivitäts-Cleanup auf dem Server).
 *
 * Wenn der Server den eigenen Spieler löscht, wird 'playerDeleted' an alle
 * Clients gebroadcastet. Hier prüfen wir, ob es unser eigener Spieler ist,
 * löschen die lokale Session und leiten zur Login-Seite weiter.
 * window.location.href wird verwendet, weil socket.js außerhalb des
 * React-Component-Trees liegt und keinen Zugriff auf React Router hat.
 */
socket.on('playerDeleted', ({ playerId }) => {
    const myPlayerId = safeStorage.getItem('playerId');
    if (myPlayerId && String(playerId) === String(myPlayerId)) {
        console.log('[socket] Eigener Spieler wurde gelöscht (Inaktivität) – Session wird geleert.');
        safeStorage.removeItem('playerId');
        safeStorage.removeItem('teamId');
        safeStorage.removeItem('isModerator');
        window.location.href = '/';
    }
});

/**
 * visibilitychange-Listener als zusätzliches Sicherheitsnetz für mobile Browser.
 *
 * iOS Safari und Android können den JS-Thread/WebSocket bei abgedunkeltem
 * Display komplett einfrieren. Wenn die Seite wieder sichtbar wird, kann
 * das 'connect'-Event schon gefeuert (und verpasst) worden sein. Daher
 * senden wir hier erneut ein playerRejoin – der Server-Handler ist idempotent
 * und verarbeitet Duplikate problemlos.
 */
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        const playerId = safeStorage.getItem('playerId');
        if (playerId && socket.connected) {
            console.log('[socket] Seite wieder sichtbar – sende playerRejoin für playerId:', playerId);
            socket.emit('playerRejoin', { playerId });
        }
    }
});

export default socket;
